import { FLOW_STEPS, type FlowStep, type SessionState } from '../types/session.types'

export type FlowStepStatus = 'complete' | 'current' | 'pending'

export interface FlowStepProgress {
  id: FlowStep
  status: FlowStepStatus
}

type ProgressState = Pick<
  SessionState,
  'appRefNo' | 'faceVerified' | 'completedSteps' | 'submittedAt'
>

function isStepComplete(step: FlowStep, state: ProgressState): boolean {
  // Submission is the terminal milestone: reaching it means every step before it
  // was completed, so say so directly rather than re-deriving it. It also keeps
  // the panel honest on the receipt, where clearApplicantData() has already
  // purged the applicant's details (ADR 0008).
  if (state.submittedAt) return true

  switch (step) {
    case 'idNumber':
      // Confirmed the moment an application exists: minting the reference is
      // what the ID-number step is for.
      return Boolean(state.appRefNo)
    case 'face':
      return state.faceVerified
    case 'submitted':
      return Boolean(state.submittedAt)
    default:
      return state.completedSteps.includes(step)
  }
}

/**
 * Sidebar fuel: each of the ten steps with its status, derived purely from
 * session state plus the step the current route says the citizen is on. The
 * current step wins over "complete" so revisiting (e.g. Manage documents)
 * highlights where the citizen actually is.
 */
export function flowProgress(state: ProgressState, currentStep: FlowStep): FlowStepProgress[] {
  return FLOW_STEPS.map((step) => ({
    id: step,
    status: step === currentStep ? 'current' : isStepComplete(step, state) ? 'complete' : 'pending',
  }))
}
